import { Component, inject, OnInit } from '@angular/core';
import { GrnService } from './grn.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HttpResponse } from '@angular/common/http';
import { SearchSelectComponent } from '../../shared/components/search-select/search-select.component';
import { VendorService } from '../vendors/vendor.service';
import { PurchaseListFilters } from '../po/po.service';
import {
  ConfigurationService,
  SETTING_KEYS,
} from '../configuration/configuration.service';
import { AuthService } from '../../auth/auth.service';
import { ToastrService } from 'ngx-toastr';
import {
  ReasonsService,
  TransferOutReason,
} from '../transfer-out-list/reason.service';

@Component({
  selector: 'app-grn',
  imports: [CommonModule, FormsModule, RouterLink, SearchSelectComponent],
  templateUrl: './grn.component.html',
  styleUrl: './grn.component.scss',
})
export class GrnComponent implements OnInit {
  private grnService = inject(GrnService);
  currentPage = 1;
  pageSize = 10;
  grnList: any[] = [];
  grnsLoading = false;
  pageCount = 1;
  searchTerm = '';

  // List filters (vendor / received-date range / status), applied
  // server-side on both the paginated list and the GRN-number search.
  private vendorService = inject(VendorService);
  vendors: any[] = [];
  readonly statusOptions = ['DRAFT', 'POSTED', 'CANCELLED'];
  filters: PurchaseListFilters = { vendorId: null, fromDate: '', toDate: '', status: '' };
  private appliedFilters = JSON.stringify(this.filters);

  // Configuration > "Allow editing posted GRNs". Off by default, so a POSTED
  // GRN offers no Edit action until the client turns it on. The backend
  // enforces the same rule on save — this only keeps the menu honest.
  private configurationService = inject(ConfigurationService);
  private authService = inject(AuthService);
  /**
   * Both must hold for the Edit action to appear on a POSTED row: the client
   * setting opens the capability, and only senior roles hold it. The server
   * checks the same two things on save.
   */
  allowEditPosted = false;

  // Configuration > "Allow cancelling posted GRNs" AND Admin. Cancelling
  // reverses the whole receipt out of stock, so it is deliberately narrower
  // than the amendment permission above. The server checks both again.
  allowCancelPosted = false;

  private reasonsService = inject(ReasonsService);
  private toastr = inject(ToastrService);
  /** Reason Master entries of type GRN, offered in the cancel dialog. */
  cancelReasons: TransferOutReason[] = [];
  /** The row being cancelled; null when the dialog is closed. */
  cancelTarget: any = null;
  cancelReasonId: string | null = null;
  cancelNote = '';
  cancelSubmitted = false;
  cancelling = false;

  ngOnInit(): void {
    const maySeniorEdit = this.authService.hasRole(['CATEGORY_MANAGER']);
    this.configurationService
      .getValue(SETTING_KEYS.GRN_ALLOW_EDIT_POSTED)
      .subscribe(
        (value) => (this.allowEditPosted = value === true && maySeniorEdit),
      );
    const mayCancel = this.authService.isSuperUser();
    this.configurationService
      .getValue(SETTING_KEYS.GRN_ALLOW_CANCEL_POSTED)
      .subscribe(
        (value) => (this.allowCancelPosted = value === true && mayCancel),
      );
    this.loadGrns();
    this.vendorService.getAllVendorsList().subscribe({
      next: (list) => (this.vendors = list || []),
      error: () => (this.vendors = []),
    });
  }

  onFilterChange() {
    const next = JSON.stringify(this.filters);
    if (next === this.appliedFilters) return;
    this.appliedFilters = next;
    this.currentPage = 1;
    if (this.searchTerm) this.searchByGrnNumber();
    else this.loadGrns();
  }

  clearFilters() {
    this.filters = { vendorId: null, fromDate: '', toDate: '', status: '' };
    this.onFilterChange();
  }

  hasActiveFilters(): boolean {
    const f = this.filters;
    return !!(f.vendorId || f.fromDate || f.toDate || f.status);
  }

  loadGrns() {
    this.grnsLoading = true;
    this.grnService
      .getGrns(
        { page: this.currentPage, limit: this.pageSize },
        this.searchTerm,
        this.filters,
      )
      .subscribe((res: any) => {
        this.grnList = res.data;
        this.pageCount = res.pageCount;
        this.grnsLoading = false;
      });
  }


  /**
   * Open the cancel dialog for one posted GRN. Reasons are loaded on demand —
   * the list is short and rarely needed, so it does not belong in ngOnInit.
   */
  openCancelDialog(grn: any): void {
    this.cancelTarget = grn;
    this.cancelReasonId = null;
    this.cancelNote = '';
    this.cancelSubmitted = false;
    if (!this.cancelReasons.length) {
      this.reasonsService.getAllReasons().subscribe({
        next: (list) =>
          (this.cancelReasons = (list || []).filter(
            (r) => r.type === 'GRN' && r.isActive !== false,
          )),
        error: () => (this.cancelReasons = []),
      });
    }
  }

  closeCancelDialog(): void {
    if (this.cancelling) return;
    this.cancelTarget = null;
  }

  /**
   * Send the cancellation. The server does the real work — reversing stock,
   * refusing when the goods have already moved on — so its message is shown
   * verbatim rather than second-guessed here.
   */
  confirmCancel(): void {
    this.cancelSubmitted = true;
    if (!this.cancelTarget || !this.cancelReasonId || this.cancelling) return;

    this.cancelling = true;
    this.grnService
      .cancelGrn(this.cancelTarget.id, {
        reasonId: this.cancelReasonId,
        reasonNote: this.cancelNote?.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.cancelling = false;
          const number = this.cancelTarget?.grnNumber ?? '';
          this.cancelTarget = null;
          this.toastr.success(
            `GRN ${number} cancelled. Its stock has been reversed.`,
            'Cancelled',
          );
          if (this.searchTerm) this.searchByGrnNumber();
          else this.loadGrns();
        },
        error: (err) => {
          this.cancelling = false;
          this.toastr.error(
            err?.error?.message || 'Could not cancel this GRN.',
            'Cancel failed',
            { timeOut: 10000 },
          );
        },
      });
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.pageCount) {
      this.currentPage = page;
      this.loadGrns();
    }
  }

  pageSizeChange() {
    this.currentPage = 1;
    this.loadGrns();
  }

  onSearch() {
    this.currentPage = 1;
    if (this.searchTerm) {
      this.searchByGrnNumber();
    } else {
      this.loadGrns();
    }
  }

  searchByGrnNumber() {
    if (!this.searchTerm) {
      this.loadGrns();
      return;
    }
    this.grnsLoading = true;
    let search = this.searchTerm.toUpperCase();
    this.grnService.searchByGrnNumber(search, this.filters).subscribe({
      next: (res: any) => {
        this.grnList = Array.isArray(res) ? res : [res];
        this.pageCount = 1;
        this.currentPage = 1;
        this.grnsLoading = false;
      },
      error: () => {
        this.grnList = [];
        this.grnsLoading = false;
      },
    });
  }

  downloadGrn(id: string) {
    this.grnService
      .downloadGrn(id)
      .subscribe((response: HttpResponse<Blob>) => {
        const blob = response.body as Blob;
        const filename = this.extractFilename(response, id);

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  private extractFilename(response: HttpResponse<Blob>, poId: string): string {
    const disposition = response.headers.get('Content-Disposition') || '';
    let match = disposition.match(/filename\*=([^;]+)/i);
    if (match && match[1]) {
      return decodeURIComponent(match[1].split("''").pop() || '');
    }
    match = disposition.match(/filename="?([^";]+)"?/i);
    if (match && match[1]) {
      return match[1];
    }
    return `grn-${poId}.pdf`;
  }

  downloadVendorReceipt(grnId: string) {
    // Get storeId from localStorage (user's primaryLocationId)
    const user = localStorage.getItem('user');
    let storeId = '';

    if (user) {
      const userData = JSON.parse(user);
      storeId = userData.storeId || '';
    }

    if (!storeId) {
      console.error('Store ID not found');
      return;
    }
    console.log(`grnId: ${grnId}, storeId: ${storeId}`);
    this.grnService
      .downloadVendorReceipt(grnId, storeId)
      .subscribe((response: HttpResponse<Blob>) => {
        const blob = response.body as Blob;
        const filename = this.extractFilename(response, grnId);

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const start = Math.max(2, this.currentPage - 2);
    const end = Math.min(this.pageCount - 1, this.currentPage + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }
}

